import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { PersonaProvider, usePersona } from "@/lib/personaStore";
import { BrandingProvider } from "@/lib/branding";
import { ViewModeProvider, useViewMode } from "@/lib/viewMode";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import PersonaPicker from "@/pages/PersonaPicker";
import BrandLandingRedirect from "@/pages/BrandLandingRedirect";
import AppLayout from "@/components/AppLayout";
import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import MyRequests from "@/pages/MyRequests";
import TicketDetail from "@/pages/TicketDetail";
import Reports from "@/pages/MyReports";
import Integrations from "@/pages/Integrations";
import Finance from "@/pages/Finance";
import Blueprint from "@/pages/Blueprint";
import Lifecycle from "@/pages/Lifecycle";
import Studio from "@/pages/Studio";
import Recovery from "@/pages/Recovery";
import AdminWarehouse from "@/pages/AdminWarehouse";
import AdminCockpit from "@/pages/AdminCockpit";
import Approvals from "@/pages/Approvals";
import ApproveToken from "@/pages/ApproveToken";
import AdminVisits from "@/pages/AdminVisits";
import AdminForecast from "@/pages/AdminForecast";
import AdminSubmissions from "@/pages/AdminSubmissions";
import AdminAIPC from "@/pages/AdminAIPC";
import AdminReports from "@/pages/AdminReports";
import AIMarketplace from "@/pages/AIMarketplace";
import AIPC from "@/pages/MyAIPC";
import VisitTracker from "@/components/VisitTracker";

function RequirePersona({ children }) {
  const { persona } = usePersona();
  if (!persona) return <Navigate to="/" replace />;
  return children;
}

// Shortcut: wraps a route element in both ProtectedRoute (auth gate) and
// anything else callers want to layer on top.
const Gated = ({ children }) => <ProtectedRoute>{children}</ProtectedRoute>;

function App() {
  return (
    <BrandingProvider>
      <ViewModeProvider>
        <AuthProvider>
          <PersonaProvider>
            <div className="App">
              <BrowserRouter>
                <VisitTracker />
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/approve/:token" element={<ApproveToken />} />
                  <Route path="/chevron" element={<BrandLandingRedirect />} />
                  <Route path="/daikin" element={<BrandLandingRedirect />} />
                  <Route path="/flexspace" element={<BrandLandingRedirect />} />

                  {/* Gated routes · demo login required */}
                  <Route path="/" element={<Gated><PersonaPicker /></Gated>} />
                  <Route path="/admin/visits" element={<Gated><AdminVisits /></Gated>} />
                  <Route path="/admin/forecast" element={<Gated><AdminForecast /></Gated>} />
                  <Route path="/admin/submissions" element={<Gated><AdminSubmissions /></Gated>} />
                  <Route path="/admin/aipc" element={<Gated><AdminAIPC /></Gated>} />
                  <Route path="/admin/reports" element={<Gated><AdminReports /></Gated>} />
                  <Route
                    element={
                      <Gated>
                        <RequirePersona>
                          <AppLayout />
                        </RequirePersona>
                      </Gated>
                    }
                  >
                    <Route path="/home" element={<Home />} />
                    <Route path="/catalogue" element={<Catalogue />} />
                    <Route path="/requests" element={<MyRequests />} />
                    <Route path="/orders" element={<Navigate to="/requests?view=orders" replace />} />
                    <Route path="/approvals" element={<Approvals />} />
                    <Route path="/tickets" element={<Navigate to="/requests?view=all" replace />} />
                    <Route path="/tickets/:id" element={<TicketDetail />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/integrations" element={<Integrations />} />
                    <Route path="/finance" element={<Finance />} />
                    <Route path="/ai-marketplace" element={<AIMarketplace />} />
                    <Route path="/aipc" element={<AIPC />} />
                    <Route path="/blueprint" element={<Blueprint />} />
                    <Route path="/lifecycle" element={<Lifecycle />} />
                    <Route path="/studio" element={<Studio />} />
                    <Route path="/recovery" element={<Recovery />} />
                    <Route path="/admin/cockpit" element={<Gated><AdminCockpit /></Gated>} />
                    <Route path="/admin/warehouse" element={<Gated><AdminWarehouse /></Gated>} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
              <Toaster richColors position="top-right" />
            </div>
          </PersonaProvider>
        </AuthProvider>
      </ViewModeProvider>
    </BrandingProvider>
  );
}

export default App;
